import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormInput } from '@/components/FormInput';
import { Button } from '@/components/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { Trans, useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import { CodeSelector } from '@/components/CodeSelector';
import { Logo } from '@/components/Logo';
import { Divider } from '@mui/material';
import { Role, useRegisterMutation } from '@/models/auth';
import { AuthMessage } from '@/features/auth/components/AuthMessages';

const FALLBACK_CODE = '+48';

type FormValues = {
  email: string;
  phoneNumber: string;
  password: string;
  passwordRepeat: string;
  companyName: string;
  role: string;
};

export const RegisterForm = () => {
  const [register] = useRegisterMutation();
  const [errorCode, setErrorCode] = useState<number | undefined>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [codeSelectedValue, setCodeSelectedValue] =
    useState<string>(FALLBACK_CODE);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      role: Role.AdPartner,
    },
  });

  const password = useRef({});
  password.current = watch('password', '');

  const onSubmit: SubmitHandler<FormValues> = async ({
    email,
    phoneNumber,
    password,
    companyName,
    role,
  }) => {
    try {
      const res = await register({
        email,
        phoneNumber: `${codeSelectedValue}${phoneNumber}`,
        password,
        companyName,
        role,
        callback: `${window.location.origin}/activate`,
      }).unwrap();
      if (res.status === 200) {
        navigate('/pending');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setErrorCode(err.data.code);
    }
  };

  const handleCodeSelect = (value: string) => {
    setCodeSelectedValue(value);
  };

  const backToLogin = () => navigate('/login');

  return (
    <div className="flex flex-1 flex-col items-center justify-between w-full">
      <div className="mb-5">
        <Logo />
      </div>
      <div className="flex flex-col items-center w-full">
        <span className="mb-6 text-xl">{t('auth.register.title')}</span>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex justify-center flex-col items-center mb-4 w-full"
        >
          <div className="w-full mb-2">
            <FormControl className="w-full flex flex-col items-center">
              <div>{t('auth.register.account_type')}</div>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    defaultValue="client"
                    className="w-full flex justify-around"
                  >
                    <FormControlLabel
                      value={Role.AdPartner}
                      control={<Radio />}
                      label={t('auth.register.client')}
                    />
                    <FormControlLabel
                      value={Role.TaxiPartner}
                      control={<Radio />}
                      label={t('auth.register.taxi')}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </div>

          <div className="w-full pb-6">
            <Divider />
          </div>

          <div className="flex w-full">
            <div className="mr-2">
              <CodeSelector
                value={codeSelectedValue}
                onSelect={handleCodeSelect}
              />
            </div>
            <FormInput
              name="phoneNumber"
              label={t('label.phone_number')}
              control={control}
              rules={{
                required: t('validation.empty'),
              }}
              error={errors.companyName}
              variant="outlined"
              fullWidth
            />
          </div>

          <div className="flex flex-row w-full">
            <FormInput
              name="email"
              label={t('auth.register.email')}
              control={control}
              rules={{
                required: t('validation.empty'),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t('auth.register.validation.email_wrong_format'),
                },
              }}
              error={errors.email}
              variant="outlined"
              sx={{ mr: '8px' }}
              fullWidth
            />
            <FormInput
              name="companyName"
              label={t('auth.register.company_name')}
              control={control}
              rules={{
                required: t('validation.empty'),
              }}
              error={errors.companyName}
              variant="outlined"
              fullWidth
            />
          </div>

          <div className="flex flex-row w-full mb-4">
            <FormInput
              name="password"
              label={t('label.password')}
              type="password"
              control={control}
              rules={{
                required: t('validation.empty'),
                minLength: {
                  value: 5,
                  message: t('auth.register.validation.password_min_length'),
                },
              }}
              error={errors.password}
              variant="outlined"
              sx={{ mr: '8px' }}
              fullWidth
            />
            <FormInput
              name="passwordRepeat"
              label={t('auth.register.password_repeat')}
              type="password"
              control={control}
              rules={{
                required: t('validation.empty'),
                validate: (value: string) => {
                  return (
                    value === password.current ||
                    t('auth.register.validation.passwords_not_match')
                  );
                },
              }}
              error={errors.passwordRepeat}
              variant="outlined"
              fullWidth
            />
          </div>

          {errorCode && <AuthMessage error={errorCode} />}

          <Button variant="contained" type="submit" fullWidth>
            {t('auth.register.register_button')}
          </Button>

          <Button
            variant="outlined"
            type="submit"
            style={{ marginTop: '15px' }}
            onClick={backToLogin}
            fullWidth
          >
            {t('label.cancel')}
          </Button>
        </form>
      </div>

      <p className="text-sm text-center text-gray-600">
        <Trans i18nKey="auth.terms_text" />
        <br />
        <Link href="https://turmsadrain.com/privacy-policy/" target="_blank">
          <Trans i18nKey="auth.privacy" />
        </Link>{' '}
        <Trans i18nKey="label.and" />{' '}
        <Link href="https://turmsadrain.com/terms-conditions/" target="_blank">
          <Trans i18nKey="auth.terms" />
        </Link>
      </p>
    </div>
  );
};
